import React, { useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { Card } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { upperCase } from 'lodash'
import { ErrorBoundary } from 'react-error-boundary'

import Layout from '../components/Layout'
import { EventCampaigns } from '../components/Recommendations/EventCampaigns'
import EventHeader from '../components/Event/Details/EventHeader'
import { initStateObject, objectReducer } from '../reducers/default'
import { changeFbCampaignStatus, changeOptimizationStatus, fetchEvent } from '../api/events'
import CampaignSetupProvider from '../components/Recommendations/Campaign/Edit/CampaignSetupProvider'
import ErrorFallback from '../components/Shared/ErrorFallback'
import AutosaveStatus from '../components/Recommendations/Campaign/Edit/AutosaveStatus'
import Switch from 'components/Shared/Controls/Switch'
import PortalModal from 'components/Shared/PortalModal'
import CampaignActivationModal from 'components/Recommendations/CampaignStatusModals/CampaignActivationModal'
import CampaignOptimizationModal from 'components/Recommendations/CampaignStatusModals/CampaignOptimizationModal'
import ErrorMessage from 'components/Shared/ErrorMessage'
import { EventCampaignStatus } from 'api/models'
import { EventStatsProvider } from 'components/Event/Details/EventStatsProvider'
import { MoreHorizIcon, RemoveIcon } from 'images'

function Header({ event }) {
    return (
        <EventHeader event={event} showActiveBadge={false}>
            <AutosaveStatus />
        </EventHeader>
    )
}

Header.propTypes = {
    event: PropTypes.object.isRequired,
}

const RightMenu = ({ onClick }) => {
    return <div className='events-insights-page_mobile-right-menu'>
        <MoreHorizIcon width={23} onClick={() => onClick(prev => !prev)}/>
    </div>
}

RightMenu.propTypes = {
    onClick: PropTypes.func
}

const Recommendation = ({ match }) => {
    const { id } = match.params
    const { t } = useTranslation()
    const history = useHistory()
    const [openList, setOpenList] = useState(false)
    const [isCampaignActive, setIsCampaignActive] = useState(false)
    const [isCampaignOptimized, setIsCampaignOptimized] = useState(false)
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false)
    const [isOptimizationModalOpen, setIsOptimizationModalOpen] = useState(false)
    const [eventState, dispatchEventAction] = useReducer(objectReducer, initStateObject)
    const [campaignStatus, statusDispatch] = useReducer(objectReducer, initStateObject)
    const [campaignOptimized, optimizationDispatch] = useReducer(objectReducer, initStateObject)
    const isMenuShown = useMemo(() => {
        if(eventState.content?.client_campaign_status)
            return Object.values(EventCampaignStatus).includes(upperCase(eventState.content?.client_campaign_status))
        return eventState.content?.has_active_campaigns
    }, [eventState.content])
    const isPastEvent = useMemo(() => moment(eventState.content?.date) < moment(), [eventState.content])


    // FIXME: Loading state
    useEffect(() => {
        fetchEvent(dispatchEventAction, id)
    }, [id])

    useEffect(() => {
        const handleResize = () => { 
            if(window.innerWidth > 991) {
                history.push(`/campaigns/?drawer-open=true&event-id=${id}`)
            }
        }

        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [history, id])
    
    useEffect(() => {
        if(campaignStatus.error)
            setIsCampaignActive(false)
        if(campaignOptimized.error)
            setIsCampaignOptimized(false)
    }, [campaignOptimized, campaignStatus])

    useEffect(() => {
        if(eventState.content) {
            const isCampaignActive = upperCase(eventState.content?.client_campaign_status) === EventCampaignStatus.active || 
                (eventState.content?.client_campaign_status === null && eventState.content?.has_active_campaigns)
            const isCampaignOptimized = upperCase(eventState.content?.campaign_optimisation_status) !== EventCampaignStatus.paused
            setIsCampaignActive(isCampaignActive)
            setIsCampaignOptimized(isCampaignOptimized)
        }
    }, [eventState.content])

    return (
        <Layout
            loggedIn
            title={'FD Lookout'}
            mobileTopBar={{ back: true, centerTitle: t('Recommendations.MainTitle'), rightMenu: isMenuShown ? <RightMenu onClick={setOpenList} /> : null, addHistoryRoute: '/campaigns', historyCallback: () => {}}}>
            <Helmet bodyAttributes={{ class: 'events-page mobile-bg-white' }} />
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <EventStatsProvider eventId={id}>
                    <CampaignSetupProvider eventId={id}>
                        <main className='events-insights-page pb-4'>
                            {isMenuShown && openList && <div>
                                <div className='backdrop' onClick={() => setOpenList(false)} />
                                <div className='dropdown-list'>
                                    <span className='close-mobile' onClick={() => setOpenList(false)}>
                                        <RemoveIcon width={35} height={35} />
                                    </span>
                                    <div className='dots-menu_each-item_wrapper border-bottom no-hover'>
                                        <div className="dots-menu_each-item_title no-icon">
                                            {isPastEvent ? t('Recommendations.campaignOptions.statuses.inactive') 
                                                : (isCampaignActive ? t('Recommendations.campaignOptions.statuses.active') : 
                                                    t('Recommendations.campaignOptions.statuses.paused'))}
                                        </div>
                                    </div>
                                    <div className='dots-menu_each-item_wrapper'>
                                        <Switch 
                                            label={t('Recommendations.campaignOptions.activateCampaign')} 
                                            onClick={() => setIsActivationModalOpen(true)}
                                            checked={isCampaignActive}
                                            disabled={isPastEvent}
                                        />
                                    </div>
                                    {isCampaignActive ? <div className='dots-menu_each-item_wrapper'>
                                        <Switch 
                                            label={t('Recommendations.campaignOptions.optimizeCampaign')} 
                                            onClick={() => setIsOptimizationModalOpen(true)}
                                            checked={isCampaignOptimized}
                                            disabled={isPastEvent}
                                        />
                                    </div> : null}
                                </div>
                            </div>}
                            <div className="container-fluid hide-scroll-bar no-padding-sm">
                                <Card className='card card-default no-inner-padding col-12 col-lg pt-4 pb-4'>
                                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                                        <Header event={eventState.content} />
                                        {campaignStatus.error &&
                                        <ErrorMessage danger>{t(campaignStatus.message)}</ErrorMessage>
                                        }
                                        {campaignOptimized.error &&
                                        <ErrorMessage danger>{t(campaignOptimized.message)}</ErrorMessage>
                                        }
                                        <EventCampaigns eventId={id} />
                                    </ErrorBoundary>
                                </Card>
                            </div>
                        </main>
                        <PortalModal isOpen={isActivationModalOpen}>
                            <CampaignActivationModal 
                                status={isCampaignActive ? 'pause' : 'activate'} 
                                onCancel={() => setIsActivationModalOpen(false)} 
                                onConfirm={() => {
                                    setIsActivationModalOpen(false)
                                    const status = isCampaignActive ? EventCampaignStatus.paused : EventCampaignStatus.active
                                    changeFbCampaignStatus(statusDispatch, eventState.content.id, status)
                                    setIsCampaignActive(prev => !prev)
                                }}
                            />
                        </PortalModal>
                        <PortalModal isOpen={isOptimizationModalOpen}>
                            <CampaignOptimizationModal
                                status={isCampaignOptimized ? 'deactivate' : 'activate'} 
                                onCancel={() => setIsOptimizationModalOpen(false)} 
                                onConfirm={() => {
                                    setIsOptimizationModalOpen(false)
                                    const status = isCampaignOptimized ? EventCampaignStatus.paused : EventCampaignStatus.active
                                    changeOptimizationStatus(optimizationDispatch, eventState.content.id, status)
                                    setIsCampaignOptimized(prev => !prev)
                                }}
                            />
                        </PortalModal>
                    </CampaignSetupProvider>
                </EventStatsProvider>
            </ErrorBoundary>
        </Layout >
    )
}

Recommendation.propTypes = {
    match: PropTypes.object
}

export default Recommendation
